<template>
  <div id="pageReporte3">
    <v-container fluid>
      <v-row>
        <v-col>
          <v-card class="elevation-2">
            <v-toolbar color="primary" dark flat class="report-toolbar">
              <v-icon class="mr-2">mdi-file-sign</v-icon>
              <v-toolbar-title>Cesiones y comodatos</v-toolbar-title>
              <v-spacer></v-spacer>
              <report-filters :fields="['period', 'location_id', 'condition', 'responsible_id']" @change="onFilterChange"></report-filters>
              <v-btn outlined :loading="loadingExport" @click="downloadExcel">
                <v-icon left small>mdi-microsoft-excel</v-icon>
                <span class="d-none d-sm-inline">Descargar Excel</span>
              </v-btn>
              <v-btn outlined class="ml-2" :loading="loadingPdf" @click="downloadPdf(3)">
                <v-icon left small>mdi-file-pdf-box</v-icon>
                <span class="d-none d-sm-inline">Descargar PDF</span>
              </v-btn>
            </v-toolbar>

            <v-card-text class="pt-6">
              <div class="text-body-2 grey--text text--darken-1 mb-6">
                Vehículos cedidos en el periodo: cuánto tiempo llevan en cesión, cuáles han superado la fecha de devolución sin
                recepcionarse y cuáles se devolvieron con retraso. El comodato se completa automáticamente con la recepción.
              </div>

              <v-progress-linear v-if="loadingPreview" indeterminate color="primary" class="mb-6"></v-progress-linear>

              <div v-else>
                <v-alert v-if="!previewItems.length" type="info" text dense>
                  No hay datos para mostrar
                </v-alert>

                <template v-else>
                  <!-- KPIs -->
                  <div class="kpi-grid mb-8">
                    <div class="kpi-card">
                      <v-icon size="30" color="blue darken-2">mdi-car-key</v-icon>
                      <div class="kpi-number">{{ kpis.enCesion }}</div>
                      <div class="kpi-label">Vehículos en cesión</div>
                    </div>
                    <div class="kpi-card">
                      <v-icon size="30" color="red darken-1">mdi-calendar-alert</v-icon>
                      <div class="kpi-number">{{ kpis.vencidas }}</div>
                      <div class="kpi-label">Cesiones vencidas</div>
                      <div class="kpi-subtitle">pasada la fecha de devolución</div>
                    </div>
                    <div class="kpi-card">
                      <v-icon size="30" color="orange darken-1">mdi-calendar-clock</v-icon>
                      <div class="kpi-number">{{ kpis.devueltasConRetraso }}</div>
                      <div class="kpi-label">Devueltas con retraso</div>
                      <div class="kpi-subtitle">recepción posterior a la fecha prevista</div>
                    </div>
                    <div class="kpi-card">
                      <v-icon size="30" color="purple darken-1">mdi-timer-sand</v-icon>
                      <div class="kpi-number">{{ kpis.mediaDias }} d</div>
                      <div class="kpi-label">Media de días en cesión</div>
                    </div>
                  </div>

                  <!-- Por ubicación de origen -->
                  <div class="report-section-title">
                    <v-icon small color="primary" class="mr-1">mdi-map-marker-outline</v-icon>
                    Por ubicación de origen
                  </div>
                  <v-simple-table class="report-table mb-8" dense>
                    <thead>
                      <tr>
                        <th>Ubicación de origen</th>
                        <th class="text-center">En cesión</th>
                        <th class="text-center">Vencidas</th>
                        <th class="text-center">Media días</th>
                        <th class="text-center">Máx. días</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="row in byOriginLocation" :key="row.location">
                        <td class="font-weight-medium">{{ row.location }}</td>
                        <td class="text-center">{{ row.enCesion }}</td>
                        <td class="text-center">
                          <span v-if="row.vencidas > 0" class="bucket-pill bucket-3">{{ row.vencidas }}</span>
                          <span v-else>{{ row.vencidas }}</span>
                        </td>
                        <td class="text-center">{{ row.mediaDias }}</td>
                        <td class="text-center">
                          <span class="bucket-pill" :class="daysBucketClass(row.maxDiasRaw)">{{ row.maxDias }}</span>
                        </td>
                      </tr>
                    </tbody>
                  </v-simple-table>

                  <!-- Detalle -->
                  <div class="report-section-title">
                    <v-icon small color="primary" class="mr-1">mdi-format-list-bulleted</v-icon>
                    Detalle de vehículos en cesión
                  </div>
                  <v-data-table
                    :headers="detailHeaders"
                    :items="detailRows"
                    class="report-table report-table--link mb-4"
                    @click:row="row => openRecord(row, 'assignment')"
                    dense
                    item-key="id"
                    :footer-props="{ itemsPerPageOptions: [10, 25, 50] }"
                    :items-per-page="10"
                  >
                    <template v-slot:item.vehicle="{ item }">
                      <div class="font-weight-medium">{{ item.vehicle }}</div>
                      <div class="text-caption grey--text">Matrícula: {{ item.plate || '—' }}</div>
                      <div class="text-caption grey--text">Bastidor: {{ item.frame || '—' }}</div>
                    </template>
                    <template v-slot:item.driver="{ item }">
                      {{ item.driver || '—' }}
                    </template>
                    <template v-slot:item.route="{ item }">
                      {{ item.origin }} <v-icon x-small>mdi-arrow-right</v-icon> {{ item.destination }}
                    </template>
                    <template v-slot:item.delivery_date="{ item }">
                      {{ formatDateOnly(item.delivery_date) }}
                    </template>
                    <template v-slot:item.end_date="{ item }">
                      {{ formatDateOnly(item.end_date) }}
                    </template>
                    <template v-slot:item.dias="{ item }">
                      <span class="bucket-pill" :class="daysBucketClass(item.diasEnCesionRaw)">{{ item.diasEnCesion }}</span>
                    </template>
                    <template v-slot:item.situacion="{ item }">
                      <v-chip small :color="item.situacion.color" :dark="item.situacion.dark" :outlined="!item.situacion.dark">
                        <v-icon v-if="item.situacion.icon" left x-small>{{ item.situacion.icon }}</v-icon>
                        {{ item.situacion.text }}
                      </v-chip>
                    </template>
                  </v-data-table>

                  <v-alert type="warning" text dense border="left" icon="mdi-shield-account-outline">
                    RGPD: el report muestra solo el nombre del conductor (inicial + apellido). No incluye DNI ni carné; esos datos
                    se consultan en el comodato con los permisos correspondientes.
                  </v-alert>
                </template>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import moment from 'moment'
import ReportService from '../../services/ReportService'
import errorHandler from '../../mixins/errorHandler'
import reportPdf from '../../mixins/reportPdf'
import reportNavigation from '../../mixins/reportNavigation'
import ReportFilters from '../../components/ReportFilters'

const DAY_SECONDS = 86400

export default {
  name: 'Reporte3',

  components: { ReportFilters },

  mixins: [errorHandler, reportPdf, reportNavigation],

  data: () => ({
    params: { period: 'this_month' },
    previewItems: [],
    loadingPreview: true,
    loadingExport: false,

    detailHeaders: [
      { text: 'Vehículo', value: 'vehicle' },
      { text: 'Conductor', value: 'driver' },
      { text: 'Ubicaciones', value: 'route', sortable: false },
      { text: 'Entrega', value: 'delivery_date' },
      { text: 'Devolución prevista', value: 'end_date' },
      { text: 'Días', value: 'dias' },
      { text: 'Situación', value: 'situacion', sortable: false }
    ]
  }),

  computed: {
    enrichedRows () {
      const nowUnix = Date.now() / 1000

      return this.previewItems.map(row => {
        const isEnProceso = row.status === 'En proceso'
        const vencida = isEnProceso && row.end_date != null && nowUnix > row.end_date
        const diasRetraso = vencida ? Math.floor((nowUnix - row.end_date) / DAY_SECONDS) : null
        const devueltaConRetraso = row.reception_date != null && row.end_date != null && row.reception_date > row.end_date

        let diasEnCesionRaw = null
        if (row.delivery_date != null) {
          if (row.reception_date != null) {
            diasEnCesionRaw = (row.reception_date - row.delivery_date) / DAY_SECONDS
          } else if (isEnProceso) {
            diasEnCesionRaw = (nowUnix - row.delivery_date) / DAY_SECONDS
          }
        }

        return {
          ...row,
          isEnProceso,
          vencida,
          diasRetraso,
          devueltaConRetraso,
          diasEnCesionRaw,
          diasEnCesion: diasEnCesionRaw !== null ? Math.floor(diasEnCesionRaw) : null
        }
      })
    },

    kpis () {
      const rows = this.enrichedRows
      const enCesion = rows.filter(row => row.isEnProceso)
      const vencidas = rows.filter(row => row.vencida)
      const devueltasConRetraso = rows.filter(row => row.devueltaConRetraso)
      const diasValues = rows.filter(row => row.diasEnCesionRaw !== null).map(row => row.diasEnCesionRaw)
      const mediaDias = diasValues.length ? (diasValues.reduce((a, b) => a + b, 0) / diasValues.length).toFixed(1).replace('.', ',') : '—'

      return {
        enCesion: enCesion.length,
        vencidas: vencidas.length,
        devueltasConRetraso: devueltasConRetraso.length,
        mediaDias
      }
    },

    byOriginLocation () {
      const rows = this.enrichedRows
      const locations = new Set()
      rows.forEach(row => { if (row.origin) locations.add(row.origin) })

      const list = Array.from(locations).map(location => {
        const locationRows = rows.filter(row => row.origin === location)
        const enCesion = locationRows.filter(row => row.isEnProceso).length
        const vencidas = locationRows.filter(row => row.vencida).length
        const diasValues = locationRows.filter(row => row.diasEnCesionRaw !== null).map(row => row.diasEnCesionRaw)
        const mediaDiasRaw = diasValues.length ? diasValues.reduce((a, b) => a + b, 0) / diasValues.length : null
        const maxDiasRaw = diasValues.length ? Math.max(...diasValues) : null

        return {
          location,
          enCesion,
          vencidas,
          mediaDias: mediaDiasRaw !== null ? mediaDiasRaw.toFixed(1).replace('.', ',') : '—',
          maxDiasRaw,
          maxDias: maxDiasRaw !== null ? Math.floor(maxDiasRaw) : '—',
          total: locationRows.length
        }
      })

      return list.sort((a, b) => b.total - a.total)
    },

    detailRows () {
      return this.enrichedRows
        .filter(row => row.isEnProceso || row.devueltaConRetraso)
        .map(row => ({ ...row, situacion: this.buildSituacion(row) }))
        .sort((a, b) => {
          const priority = (row) => {
            if (row.vencida) return 0
            if (row.devueltaConRetraso) return 1
            return 2
          }
          const priorityDiff = priority(a) - priority(b)
          if (priorityDiff !== 0) return priorityDiff
          return (b.diasEnCesionRaw || 0) - (a.diasEnCesionRaw || 0)
        })
    }
  },

  methods: {
    onFilterChange (params) {
      this.params = params
      this.loadPreview()
    },

    buildSituacion (row) {
      if (row.vencida) {
        return { text: `Vencida ${row.diasRetraso} d`, color: 'red', dark: true, icon: 'mdi-calendar-alert' }
      }
      if (row.devueltaConRetraso) {
        return { text: 'Devuelta con retraso', color: 'red', dark: false, icon: 'mdi-alert-outline' }
      }
      if (row.isEnProceso) {
        return { text: 'En plazo', color: 'grey darken-1', dark: true, icon: null }
      }
      return { text: 'Completada', color: 'green', dark: true, icon: 'mdi-check' }
    },

    daysBucketClass (days) {
      if (days === null || days === undefined) return ''
      if (days <= 2) return 'bucket-0'
      if (days <= 7) return 'bucket-1'
      if (days <= 15) return 'bucket-2'
      return 'bucket-3'
    },

    formatDateOnly (value) {
      if (!value) return '—'
      return moment.unix(value).format('DD/MM/YYYY')
    },

    async loadPreview () {
      try {
        this.loadingPreview = true
        const { data } = await ReportService.getReport3(this.params)
        this.previewItems = data.data || []
      } catch (error) {
        this.handleApiError(error, 'Error al cargar los datos del reporte')
      } finally {
        this.loadingPreview = false
      }
    },

    async downloadExcel () {
      try {
        this.loadingExport = true
        const response = await ReportService.downloadReport3(this.params)
        const blob = response.data

        if (!blob || blob.size === 0) {
          throw new Error('El archivo descargado está vacío')
        }

        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')
        link.download = `reporte3_${timestamp}.xlsx`
        link.style.display = 'none'

        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)

        this.$toast.success('Reporte descargado correctamente')
      } catch (error) {
        this.handleApiError(error, 'Error al descargar el reporte')
      } finally {
        this.loadingExport = false
      }
    }
  },

  created () {
    this.loadPreview()
  }
}
</script>

<style scoped>
.report-table--link >>> tbody tr {
  cursor: pointer;
}

.kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 16px;
}

.kpi-card {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px 12px;
  text-align: center;
}

.kpi-number {
  font-size: 30px;
  font-weight: 600;
  line-height: 1.3;
  margin-top: 4px;
}

.kpi-label {
  font-size: 14px;
  color: rgba(0, 0, 0, 0.7);
}

.kpi-subtitle {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.5);
}

.report-section-title {
  display: flex;
  align-items: center;
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 12px;
}

.report-table {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
}

.report-table >>> thead th {
  background-color: #fafafa;
  font-weight: 600 !important;
}

.bucket-pill {
  display: inline-block;
  min-width: 28px;
  padding: 2px 8px;
  border-radius: 12px;
  font-weight: 500;
  text-align: center;
}

.bucket-0 {
  background-color: #E8F5E9;
  color: #2E7D32;
}

.bucket-1 {
  background-color: #FFF8E1;
  color: #F9A825;
}

.bucket-2 {
  background-color: #FFF3E0;
  color: #EF6C00;
}

.bucket-3 {
  background-color: #FFEBEE;
  color: #C62828;
}
</style>
